import {
  ChatBubbleLeftEllipsisIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
  TagIcon,
  TicketIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import "../CSS/TicketList.css";
import IssueForm from "./IssueForm";
import IssueFormV2 from "./IssueFormV2";
import PageLoader from "./pageLoaders/PageLoader";


const ChatList = ({
  createNewChat,
  setTicketId,
  ticketId,
  conversations,
  setSelectedConversation,
  setView,
  isExpanded,
  setIsExpanded,
  setIsOpen,
  showTicketForm,
  setShowTicketForm,
  loading,
  setLoading,
  widgetConfigs,
  setupDetails,
}) => {
  const [internalShowForm, setInternalShowForm] = useState(false);
  const isFormOpen = showTicketForm !== undefined ? showTicketForm : internalShowForm;
  const setFormOpen = setShowTicketForm || setInternalShowForm;

  // Tabs: only "open" and "closed"
  const [activeTab, setActiveTab] = useState("open");

  useEffect(() => {
    const savedTab = localStorage.getItem("dexkor-chat-widget-ticket-tab");
    if (savedTab === "open" || savedTab === "closed") {
      setActiveTab(savedTab);
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("dexkor-chat-widget-ticket-tab", tab);
  };

  const handleCreateNewChat = () => {
    setFormOpen(true);
  };

  const handleFormSubmit = (formData) => {
    createNewChat(formData);
    setFormOpen(false);
  };

  // Filter ticket conversations - exclude only those with subSource === "convo"
  const isTicketConversation = (conversation) => {
    if (!conversation || !conversation.ticketId) return false;

    const ticket = conversation.ticketId;
    const subSource = (ticket?.subSource || conversation?.subSource || "").toLowerCase().trim();

    if (subSource === "convo") {
      return false;
    }

    return true;
  };

  const ticketConversations = (conversations || []).filter(isTicketConversation);

  // Status classification for Open vs Closed
  const isClosedStatus = (status) => {
    const s = String(status || "").toLowerCase().trim();
    return ["closed", "cancelled", "duplicate", "merged"].includes(s);
  };

  const isOpenStatus = (status) => {
    return !isClosedStatus(status);
  };

  const openTickets = ticketConversations.filter((c) => isOpenStatus(c?.ticketId?.status));
  const closedTickets = ticketConversations.filter((c) => isClosedStatus(c?.ticketId?.status));

  const displayedConversations = (activeTab === "closed" ? closedTickets : openTickets).sort(
    (a, b) => {
      const timeA = new Date(
        a?.ticketId?.lastMessageAt || a?.updatedAt || a?.createdAt || 0
      ).getTime();
      const timeB = new Date(
        b?.ticketId?.lastMessageAt || b?.updatedAt || b?.createdAt || 0
      ).getTime();
      return timeB - timeA;
    }
  );

  const handleSelectTicket = (conversation) => {
    setSelectedConversation(conversation);
    setView("message");
  };

  // Enterprise status color mapping with status indicator dot
  const getStatusBadge = (status) => {
    const s = String(status || "open").toLowerCase().trim();
    let dotColor = "bg-blue-500";
    let badgeClasses = "bg-blue-50 text-blue-700 border-blue-200/80";
    let label = status?.replace(/_/g, " ") || "Open";

    switch (s) {
      case "in_progress":
        dotColor = "bg-indigo-500 animate-pulse";
        badgeClasses = "bg-indigo-50 text-indigo-700 border-indigo-200/80";
        label = "In Progress";
        break;
      case "awaiting_customer":
        dotColor = "bg-amber-500";
        badgeClasses = "bg-amber-50 text-amber-700 border-amber-200/80";
        label = "Waiting on you";
        break;
      case "awaiting_third_party":
        dotColor = "bg-violet-500";
        badgeClasses = "bg-violet-50 text-violet-700 border-violet-200/80";
        label = "In Review";
        break;
      case "snoozed":
        dotColor = "bg-purple-500";
        badgeClasses = "bg-purple-50 text-purple-700 border-purple-200/80";
        label = "Snoozed";
        break;
      case "resolved":
        dotColor = "bg-emerald-500";
        badgeClasses = "bg-emerald-50 text-emerald-700 border-emerald-200/80";
        label = "Resolved";
        break;
      case "closed":
        dotColor = "bg-slate-400";
        badgeClasses = "bg-slate-100 text-slate-700 border-slate-200";
        label = "Closed";
        break;
      case "reopen":
        dotColor = "bg-amber-500";
        badgeClasses = "bg-amber-50 text-amber-700 border-amber-200/80";
        label = "Reopened";
        break;
      case "cancelled":
        dotColor = "bg-rose-500";
        badgeClasses = "bg-rose-50 text-rose-700 border-rose-200/80";
        label = "Cancelled";
        break;
      case "assigned":
        dotColor = "bg-blue-500";
        badgeClasses = "bg-blue-50 text-blue-700 border-blue-200/80";
        label = "Assigned";
        break;
      case "unassigned":
      case "open":
      default:
        dotColor = "bg-emerald-500";
        badgeClasses = "bg-emerald-50 text-emerald-700 border-emerald-200/80";
        label = "Open";
        break;
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badgeClasses} shadow-2xs select-none`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
        <span className="capitalize">{label}</span>
      </span>
    );
  };

  // Enterprise priority tag with micro indicator & distinct colors
  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    const p = String(priority).toLowerCase().trim();
    let dotColor = "bg-slate-400";
    let bgClasses = "bg-slate-50 text-slate-600 border-slate-200/80";
    let label = priority;

    switch (p) {
      case "urgent":
        dotColor = "bg-rose-500 animate-pulse";
        bgClasses = "bg-rose-50 text-rose-700 border-rose-200/90";
        label = "Urgent";
        break;
      case "high":
        dotColor = "bg-orange-500";
        bgClasses = "bg-orange-50 text-orange-700 border-orange-200/90";
        label = "High";
        break;
      case "medium":
      case "normal":
        dotColor = "bg-amber-500";
        bgClasses = "bg-amber-50 text-amber-800 border-amber-200/90";
        label = "Medium";
        break;
      case "low":
        dotColor = "bg-emerald-500";
        bgClasses = "bg-emerald-50 text-emerald-700 border-emerald-200/90";
        label = "Low";
        break;
      default:
        dotColor = "bg-slate-400";
        bgClasses = "bg-slate-50 text-slate-600 border-slate-200/80";
        break;
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${bgClasses} select-none shadow-2xs`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
        <span className="capitalize">{label}</span>
      </span>
    );
  };

  const formatSubject = (subject) => {
    if (!subject) return "No subject provided";
    return subject.trim();
  };

  const formatTimeOrDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24 && date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  if (isFormOpen) {
    const formVersion =
      setupDetails?.widgetCustomizations?.formVersion ||
      setupDetails?.widgetCustomizations?.ticketFormType ||
      widgetConfigs?.formVersion;

    const isV2Form =
      formVersion === "v2" ||
      formVersion === "new" ||
      formVersion === "canvas";

    if (isV2Form) {
      return (
        <IssueFormV2
          onSubmit={handleFormSubmit}
          setShowForm={setFormOpen}
          ticketId={ticketId}
          setTicketId={setTicketId}
          loading={loading}
          setLoading={setLoading}
          widgetConfigs={widgetConfigs}
          setupDetails={setupDetails}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          setIsOpen={setIsOpen}
        />
      );
    }

    return (
      <IssueForm
        onSubmit={handleFormSubmit}
        setShowForm={setFormOpen}
        ticketId={ticketId}
        setTicketId={setTicketId}
        loading={loading}
        setLoading={setLoading}
        widgetConfigs={widgetConfigs}
        setupDetails={setupDetails}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        setIsOpen={setIsOpen}
      />
    );
  }

  const uiColor =
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    setupDetails?.widgetCustomizations?.primaryColor ||
    "#0066FE";

  return (
    <div className="h-full flex-1 min-h-0 flex flex-col relative bg-slate-50/40">
      {/* Sleek Enterprise Tabs & New Ticket Bar */}
      <div className="px-4 py-2.5 bg-white border-b border-slate-100 flex items-center justify-between gap-3 shrink-0">
        {/* Segmented Control Switcher: OPEN vs CLOSED ONLY */}
        <div className="flex items-center p-0.5 bg-slate-100/90 rounded-xl border border-slate-200/50">
          <button
            type="button"
            onClick={() => handleTabChange("open")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer border-none outline-none ${
              activeTab === "open"
                ? "bg-white text-slate-900 shadow-2xs"
                : "bg-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span>Open</span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full transition-colors ${
                activeTab === "open"
                  ? "bg-blue-50 text-blue-700"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {openTickets.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange("closed")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer border-none outline-none ${
              activeTab === "closed"
                ? "bg-white text-slate-900 shadow-2xs"
                : "bg-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span>Closed</span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full transition-colors ${
                activeTab === "closed"
                  ? "bg-slate-200 text-slate-800"
                  : "bg-slate-200/70 text-slate-600"
              }`}
            >
              {closedTickets.length}
            </span>
          </button>
        </div>

        {/* Compact Enterprise "+ New Ticket" Button */}
        <button
          type="button"
          onClick={handleCreateNewChat}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white text-xs font-semibold shadow-xs hover:opacity-95 active:scale-95 transition-all duration-150 cursor-pointer border-none shrink-0"
          style={{ background: uiColor }}
        >
          <PlusIcon className="w-3.5 h-3.5 stroke-[2.5]" />
          <span>New Ticket</span>
        </button>
      </div>

      {/* Tickets Scrollable List */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto dexkor-scrollbar px-3 py-3 pb-24">
          {loading ? (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <PageLoader />
            </div>
          ) : displayedConversations.length > 0 ? (
            <div className="space-y-2">
              {displayedConversations.map((conversation, index) => {
                const ticket = conversation?.ticketId;
                const messageCount = (conversation?.messages?.length || 0) + 1;
                const lastUpdate =
                  ticket?.lastMessageAt || conversation?.updatedAt || conversation?.createdAt;

                return (
                  <div
                    key={conversation._id || ticket?._id || index}
                    onClick={() => handleSelectTicket(conversation)}
                    className="group bg-white hover:bg-slate-50/80 rounded-xl border border-slate-200/80 hover:border-slate-300 p-3 shadow-2xs hover:shadow-xs transition-all duration-150 cursor-pointer"
                  >
                    {/* Top Row: Title + Time */}
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className="text-xs sm:text-[13px] font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug line-clamp-1 flex-1 text-left">
                        {formatSubject(ticket?.subject)}
                      </h3>
                      <span className="text-[11px] font-medium text-slate-400 shrink-0 whitespace-nowrap pt-0.5">
                        {formatTimeOrDate(conversation.createdAt)}
                      </span>
                    </div>

                    {/* Middle Row: Ticket # + Status + Priority */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      {ticket?.ticketNumber && (
                        <span className="font-mono text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200/60 px-1.5 py-0.5 rounded-md">
                          #{ticket.ticketNumber}
                        </span>
                      )}
                      {getStatusBadge(ticket?.status)}
                      {getPriorityBadge(ticket?.priority)}
                    </div>

                    {/* Bottom Row: Reply count + Last updated + Arrow */}
                    <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1.5 border-t border-slate-100/80">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100/80 px-1.5 py-0.5 rounded">
                          <ChatBubbleLeftEllipsisIcon className="w-3 h-3 text-slate-400" />
                          <span>{messageCount}</span>
                        </span>

                        {lastUpdate && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                            <ClockIcon className="w-3 h-3 text-slate-400" />
                            <span>Updated: {formatTimeOrDate(lastUpdate)}</span>
                          </span>
                        )}
                      </div>

                      <ChevronRightIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[240px] p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200/80 flex items-center justify-center mb-3 shadow-2xs">
                <TicketIcon className="w-6 h-6 text-slate-400 stroke-[1.5]" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-1">
                {activeTab === "open" ? "No open tickets" : "No closed tickets"}
              </h3>
              <p className="text-xs text-slate-500 mb-4 max-w-[220px] leading-relaxed">
                {activeTab === "open"
                  ? "You're all caught up! Need help with an issue or have a question?"
                  : "You don't have any closed or resolved tickets yet."}
              </p>
              {activeTab === "open" && (
                <button
                  type="button"
                  onClick={handleCreateNewChat}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-white text-xs font-semibold shadow-xs hover:opacity-95 active:scale-95 transition-all cursor-pointer border-none"
                  style={{ background: uiColor }}
                >
                  <PlusIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>Create a ticket</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatList;
